import css from './views.module.css'
import { Card, ViewHeader, Empty, QueryError, MicroLabel, Notice } from '../ui'
import { useRuns } from '../data/queries'
import { summarise } from '../data/map/costs'
import { usd, tokens } from '../lib/money'

/**
 * cost_usd is authoritative and nothing here derives money from tokens.
 *
 * The spec's envelope card ($41.86 of a $120 daily envelope, with the stacked
 * departmental bar) is not built: no table holds an envelope, so there is no
 * denominator and the bar would be a share of a number we invented. Spend, runs
 * and the per-department split are all real and are what the view shows.
 */
export function Costs() {
  const runs = useRuns()
  if (runs.error) return <QueryError error={runs.error} />
  if (!runs.data) return <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading runs…</div></Card>

  const s = summarise(runs.data)

  if (!s.hasAnyCost) {
    return (
      <>
        <ViewHeader title="Costs" />
        <Card><Empty glyph="[-_-]">No run has recorded a cost yet.</Empty></Card>
      </>
    )
  }

  return (
    <>
      <ViewHeader title="Costs" aside={`${runs.data.length} ${runs.data.length === 1 ? 'run' : 'runs'} on record`} />

      <Card style={{ padding: '18px 22px', marginBottom: 14 }}>
        <MicroLabel>Spend today</MicroLabel>
        <div className="mono" style={{ fontSize: 28, fontWeight: 500, marginTop: 7 }}>
          {usd(s.todaySpend, { precise: s.todaySpend < 1 })}
        </div>
        <Notice>
          No daily or monthly envelope exists in the schema, so this is spend without a
          denominator. The envelope card in the design needs a table to read from before it can
          say anything true.
        </Notice>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div className={css.costHead}>
          <MicroLabel>Department</MicroLabel>
          <div className={`${css.num} microLabel`}>Runs</div>
          <div className={`${css.num} microLabel`}>Output tok</div>
          <div className={`${css.num} microLabel`}>Cache read</div>
          <div className={`${css.num} microLabel`}>Spend</div>
        </div>
        {s.byDepartment.map((row) => (
          <div key={row.department} className={css.costRow}>
            <div style={{ fontSize: 14 }}>{row.department}</div>
            <div className={`${css.num} mono`}>{row.runs}</div>
            <div className={`${css.num} mono`}>{tokens(row.outputTokens)}</div>
            <div className={`${css.num} mono`}>{tokens(row.cacheReadTokens)}</div>
            <div className={`${css.num} mono`}>{usd(row.spend, { precise: row.spend < 1 })}</div>
          </div>
        ))}
      </Card>

      <Card>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <MicroLabel>Most expensive runs</MicroLabel>
        </div>
        {s.topRuns.map((run) => (
          <div key={run.id} className={css.topRow}>
            {/* A build run and its review share an issue, a title and an
                outcome, so the reference alone makes two rows identical. The
                agent is what tells them apart. */}
            <span className="mono" style={{ fontSize: 12 }}>
              {run.external_url ? (
                <a href={run.external_url} target="_blank" rel="noreferrer">{run.external_ref ?? run.id.slice(0, 8)}</a>
              ) : (
                run.external_ref ?? run.id.slice(0, 8)
              )}
              {run.agent ? (
                <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)' }}>{run.agent}</span>
              ) : null}
            </span>
            <span style={{ fontSize: 14 }}>{run.title ?? '—'}</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{run.status.replace(/_/g, ' ')}</span>
            <span className={`${css.num} mono`}>{usd(run.cost_usd, { precise: true })}</span>
          </div>
        ))}
        <Notice>
          <span style={{ padding: '0 18px', display: 'block' }}>
            Attribution is per run. Three token figures are tracked separately — raw input, cache
            creation and cache read — because they are priced differently; only cost_usd is money.
          </span>
        </Notice>
      </Card>
    </>
  )
}
